import { PureComponent, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cns from 'classnames';
import { withRouter } from 'react-router-dom';
import { reduxForm } from 'redux-form';
import Button from 'react-bootstrap/lib/Button';

import { withTracker, hitTypes } from 'helpers/tracking';
import Section from 'components/Section';
import Head from 'components/Head';
import Layout from 'components/page/Layout';
import FavoriteIcon from 'components/FavoriteIcon';
import HideIcon from 'components/icons/Hide.com.svg';
import Breadcrumbs from 'components/Breadcrumbs';
import BackLink from 'components/BackLink';
import { formatMarkdown } from 'helpers/i18n';
import AdList from './AdList';
import NoResultsBlock from './NoResultsBlock';
import { reducer } from './duck';
import * as actions from './actions';
import {
  FAVORITES_OVERVIEW_FORM,
  FAVORITE_TYPE_LIKE,
  FAVORITE_TYPE_DISLIKE,
} from './constants';
import mapStateToProps from './mapStateToProps';
import cn from './styles.local.less';

const dislikeValidityText = formatMarkdown(
  __('favorites.overview.dislikeValidityText'),
);

class FavoritesOverview extends PureComponent {
  static propTypes = {
    layoutError: PropTypes.object,
    head: PropTypes.object,
    backBtnUrl: PropTypes.string.isRequired,
    breadcrumbs: PropTypes.array.isRequired,
    dislikeCount: PropTypes.number.isRequired,
    likeCount: PropTypes.number.isRequired,
    noResultsBlockProps: PropTypes.object.isRequired,
    applySort: PropTypes.func.isRequired,
    loadPage: PropTypes.func.isRequired,
    adListProps: PropTypes.object.isRequired,
    toggleAdFavorite: PropTypes.func.isRequired,
    toggleAdDislike: PropTypes.func.isRequired,
    removeFavoriteForUnpublishedAd: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    goToFavorites: PropTypes.func.isRequired,
    goToDislikes: PropTypes.func.isRequired,
    favoriteType: PropTypes.string.isRequired,
    showAdList: PropTypes.bool.isRequired,
    tracker: PropTypes.object.isRequired,
  };

  handleSortChanged = () => {
    const { applySort } = this.props;

    applySort();
  };

  handleLoadPageClick = (...args) => {
    const { loadPage } = this.props;

    return loadPage(...args);
  };

  handleFavoritesTabClick = () => {
    const { goToFavorites, tracker } = this.props;

    tracker.track(hitTypes.FAVORITES_OVERVIEW_LIKE_TAB_CLICK);
    goToFavorites();
  };

  handleDislikesTabClick = () => {
    const { goToDislikes, tracker } = this.props;

    tracker.track(hitTypes.FAVORITES_OVERVIEW_DISLIKE_TAB_CLICK);
    goToDislikes();
  };

  renderLikeTabTitle() {
    const { likeCount } = this.props;

    return (
      <Button
        onClick={this.handleFavoritesTabClick}
        bsStyle="link"
        className={cn.tabHead}
      >
        <div className={cn.likeIconWrapper}>
          <FavoriteIcon isFilled className={cn.tabIcon} />
        </div>
        <span className={cns(cn.tabTitle, 'hidden-xs')}>
          {__('favorites.overview.tabs.likeTitle')}
        </span>
        <span className={cns(cn.tabTitle, 'hidden visible-xs')}>
          {__('favorites.overview.tabs.mobileLikeTitle')}
        </span>
        &nbsp;({likeCount})
      </Button>
    );
  }

  renderDislikeTabTitle() {
    const { dislikeCount } = this.props;

    return (
      <Button
        onClick={this.handleDislikesTabClick}
        bsStyle="link"
        className={cn.tabHead}
      >
        <div className={cn.dislikeIconWrapper}>
          <HideIcon className={cn.tabIcon} />
        </div>
        <span className={cns(cn.tabTitle, 'hidden-xs')}>
          {__('favorites.overview.tabs.dislikeTitle')}
        </span>
        <span className={cns(cn.tabTitle, 'hidden visible-xs')}>
          {__('favorites.overview.tabs.mobileDislikeTitle')}
        </span>
        &nbsp;({dislikeCount})
      </Button>
    );
  }

  renderContent() {
    const {
      adListProps,
      breadcrumbs,
      favoriteType,
      noResultsBlockProps,
      head,
      removeFavoriteForUnpublishedAd,
      showAdList,
      toggleAdFavorite,
      toggleAdDislike,
      isLoading,
    } = this.props;

    return (
      <Fragment>
        <Head {...head} />
        <div className="back-link-container">
          <BackLink>{__('common.backLink')}</BackLink>
        </div>

        <div className={cn.root}>
          <ul className="nav nav-tabs">
            <li
              className={cns(cn.tab, {
                [cn.active]: favoriteType === FAVORITE_TYPE_LIKE,
              })}
            >
              {this.renderLikeTabTitle()}
            </li>
            <li
              className={cns(cn.tab, {
                [cn.active]: favoriteType === FAVORITE_TYPE_DISLIKE,
              })}
            >
              {this.renderDislikeTabTitle()}
            </li>
          </ul>

          {favoriteType === FAVORITE_TYPE_DISLIKE ? (
            <div
              className={cn.dislikeValitity}
              dangerouslySetInnerHTML={{ __html: dislikeValidityText }}
            />
          ) : null}

          <div className={cns({ [cn.fade]: isLoading })}>
            {showAdList ? (
              <AdList
                {...adListProps}
                onToggleAdFavoriteClick={toggleAdFavorite}
                onToggleAdDislikeClick={toggleAdDislike}
                onRemoveFavoriteForUnpublishedAd={
                  removeFavoriteForUnpublishedAd
                }
                loadPage={this.handleLoadPageClick}
              />
            ) : (
              <NoResultsBlock {...noResultsBlockProps} />
            )}
          </div>
        </div>

        <Section>
          <Breadcrumbs items={breadcrumbs} className={cn.breadcrumbs} />
        </Section>
      </Fragment>
    );
  }

  render() {
    const { layoutError } = this.props;

    return <Layout content={this.renderContent()} error={layoutError} />;
  }
}

export default withTracker(
  withRouter(
    connect(
      mapStateToProps,
      actions,
    )(
      reduxForm({
        form: FAVORITES_OVERVIEW_FORM,
        enableReinitialize: true,
      })(FavoritesOverview),
    ),
  ),
);

export { actions, reducer };
